import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { GoogleBusinessSection } from '../components/GoogleBusiness';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success(t('contact.successToast'));
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.errorToast'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: 'construction', label: t('contact.serviceOptions.construction') },
    { value: 'equipment', label: t('contact.serviceOptions.equipment') },
    { value: 'worksite', label: t('contact.serviceOptions.worksite') },
    { value: 'transport', label: t('contact.serviceOptions.transport') },
    { value: 'concrete', label: t('contact.serviceOptions.concrete') },
    { value: 'excavation', label: t('contact.serviceOptions.excavation') },
    { value: 'welding', label: t('contact.serviceOptions.welding') },
    { value: 'other', label: t('contact.serviceOptions.other') },
  ];

  return (
    <div className="grain-texture pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4 animate-fade-in-up">
              {t('contact.tagline')}
            </p>
            <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
              {t('contact.title1')} <span className="text-primary">{t('contact.title2')}</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed animate-fade-in-up animation-delay-200">
              {t('contact.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-light tracking-tight mb-8">
                {t('contact.discussProject')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-12">
                {t('contact.discussDesc')}
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">{t('contact.serviceAreas')}</h3>
                    <p className="text-muted-foreground">
                      Lanaudière, Laurentians, Laval & Montreal, QC
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">{t('contact.email')}</h3>
                    <a href="mailto:info@kenscho.ca" className="text-primary hover:underline">
                      info@kenscho.ca
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">{t('contact.phone')}</h3>
                    <ObfuscatedPhone className="text-primary hover:underline" />
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">{t('contact.businessHours')}</h3>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {t('contact.hoursText')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-card border border-border/60 rounded-lg p-8 md:p-10">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="success-message">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-medium mb-4">{t('contact.thankYou')}</h3>
                  <p className="text-muted-foreground mb-8">
                    {t('contact.receivedMessage')}
                  </p>
                  <Button 
                    variant="outline" 
                    className="rounded-full"
                    onClick={() => setIsSubmitted(false)}
                  >
                    {t('contact.sendAnother')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="text-2xl font-medium mb-6">{t('contact.sendMessage')}</h3>
                  
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">{t('contact.fullName')} *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          required
                          className="mt-2"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">{t('contact.emailAddress')} *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="mt-2"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">{t('contact.phoneNumber')}</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(514) 555-1234"
                          className="mt-2"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="service">{t('contact.serviceInterested')}</Label>
                        <Select onValueChange={handleServiceChange} value={formData.service}>
                          <SelectTrigger className="mt-2" data-testid="select-service">
                            <SelectValue placeholder={t('contact.selectService')} />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">{t('contact.yourMessage')} *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact.messagePlaceholder')}
                        rows={5}
                        required
                        className="mt-2"
                        data-testid="input-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full rounded-full btn-animate"
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        t('contact.sending')
                      ) : (
                        <>
                          {t('contact.submit')} <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Business Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <GoogleBusinessSection showMap={true} />
        </div>
      </section>
    </div>
  );
}
